import React, { useEffect, useMemo, useState } from 'react';
import { CustomButton, showToaster, toasterTypes } from '../../widgets';
import ModalComponent from '../common/Modal/modal';
import { FieldItem } from '../../widgets/fields';
import useFieldItem from '../../widgets/fields/UseFieldItem';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../store/slices/auth';
import { savePrintableTemplate, updatePrintableTemplate } from './apiController';
import { printableTemplateElements } from './FormElements';

const CreateTemplate = (props) => {
    const { projectData, forms, onClose, previousState, isUpdate } = props;
    const dispatch = useDispatch();
    const formsList = forms || [];
    const [sections, setSections] = useState([{ id: Date.now() }]);
    const [formValues, setFormValues] = useState([]);

    const initialValues = useMemo(() => {
        if (isUpdate && previousState) {
            const values: any = {
                templateTitle: previousState.title || ''
            };
            previousState.forms?.forEach((section, index) => {
                values[`sectionTitle${index}`] = section.section_title || '';

                const selectedForm = formsList.find(form => form.id === section.form_id);
                values[`formSelect${index}`] = selectedForm
                    ? { label: selectedForm.form_name, value: selectedForm.id }
                    : null;
            });

            return values;
        }

        return {
            sectionTitle0: '',
            formSelect0: null,
            templateTitle: ''
        };
    }, [isUpdate, previousState, formsList]);

    const [values, fields, handleChange, { validateValues, updateValue }] = useFieldItem(
        printableTemplateElements,
        initialValues,
        { onValueChange }
    );

    useEffect(() => {
        processFormValues();

        if (isUpdate && previousState?.forms) {
            const existingSections = previousState.forms.map((_, index) => ({
                id: Date.now() + index
            }));
            setSections(existingSections);
        }
    }, []);

    const processFormValues = () => {
        let formDropdownValues = formsList.map((form) => ({
            label: form.form_name,
            value: form.id
        }));
        setFormValues(formDropdownValues);
    };

    function onValueChange(name, value, values, fieldValues = null) {
        return [{
            ...values,
            [name]: value,
        }];
    }

    const addSection = () => {
        setSections(prev => [...prev, { id: Date.now() }]);
    };

    const removeSection = (index) => {
        if (sections.length > 1) {
            const newSections = sections.filter((_, i) => i !== index);
            setSections(newSections);
        }
    };

    const onSave = async () => {
        const requiredFields = ["templateTitle"];

        sections.forEach((_, index) => {
            requiredFields.push(`sectionTitle${index}`);
            requiredFields.push(`formSelect${index}`);
        });

        if (validateValues(requiredFields)) {
            showToaster(toasterTypes.ERROR, "All fields are mandatory");
        } else {
            const sectionsPayload = sections.map((section, index) => ({
                section_title: values[`sectionTitle${index}`],
                form_id: values[`formSelect${index}`]?.value || values[`formSelect${index}`]
            }));

            const topicId = projectData?.TopicID;
            const payload = {
                title: values["templateTitle"],
                forms: sectionsPayload,
                topic_id: topicId
            };
            let templateId;

            if (isUpdate) {
                templateId = previousState.id;
            }

            try {
                dispatch(setLoading(true));
                let response;

                if (isUpdate) {
                    response = await updatePrintableTemplate(templateId, payload);
                } else {
                    response = await savePrintableTemplate(payload);
                }

                if (response.status || response.success) {
                    showToaster(
                        toasterTypes.SUCCESS,
                        response?.message || `Template ${isUpdate ? 'updated' : 'added'} successfully`
                    );
                    onClose();
                }
            } catch (error) {
                showToaster(
                    toasterTypes.ERROR,
                    `Unable to ${isUpdate ? 'update' : 'save'} template`
                );
            } finally {
                dispatch(setLoading(false));
            }
        }
    };

    return (
        <ModalComponent
            width={window.innerWidth > 700 ? "800px" : "350px"}
            isOpen={true}
            onClose={() => onClose()}
            header={isUpdate ? "Update Template" : "Create Template"}
            centered={true}
            customClass='printable-template-form-modal'
            body={
                <div className="printable-template-form-body">
                    <div className="template-title-section">
                        <FieldItem
                            {...printableTemplateElements.templateTitle}
                            name={`templateTitle`}
                            value={values[`templateTitle`] || ''}
                            onChange={(...e) => handleChange(`templateTitle`, ...e)}
                            touched={fields[`templateTitle`] && fields[`templateTitle`].hasError}
                            error={fields[`templateTitle`] && fields[`templateTitle`].errorMsg}
                        />
                    </div>

                    <div className="sections-container">
                        {sections.map((section, index) => (
                            <div key={section.id} className="section-item">
                                <div className="section-header">
                                    <h4 className="section-number">Section {index + 1}</h4>
                                    {sections.length > 1 && (
                                        <button
                                            type="button"
                                            className="remove-section-btn"
                                            onClick={() => removeSection(index)}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>

                                <div className="section-fields">
                                    <FieldItem
                                        {...printableTemplateElements.sectionTitle}
                                        name={`sectionTitle${index}`}
                                        value={values[`sectionTitle${index}`] || ''}
                                        onChange={(...e) => handleChange(`sectionTitle${index}`, ...e)}
                                        touched={fields[`sectionTitle${index}`] && fields[`sectionTitle${index}`].hasError}
                                        error={fields[`sectionTitle${index}`] && fields[`sectionTitle${index}`].errorMsg}
                                    />

                                    <FieldItem
                                        {...printableTemplateElements.formSelect}
                                        name={`formSelect${index}`}
                                        value={values[`formSelect${index}`] || null}
                                        values={formValues}
                                        onChange={(...e) => handleChange(`formSelect${index}`, ...e)}
                                        touched={fields[`formSelect${index}`] && fields[`formSelect${index}`].hasError}
                                        error={fields[`formSelect${index}`] && fields[`formSelect${index}`].errorMsg}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="add-section-container">
                        <CustomButton
                            type="primary"
                            text="+ Add Section"
                            onClick={addSection}
                        />
                    </div>
                </div>
            }
            footer={
                <div className="printable-template-form-footer">
                    <CustomButton
                        type="primary"
                        text={isUpdate ? "Update" : "Save"}
                        onClick={() => onSave()}
                    />
                </div>
            }
        />
    );
};

export default CreateTemplate;